import { useEffect, useId, useMemo, useState } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { Icono } from '../componentes/Iconos.js';
import { responsables, ZONAS } from './datos.js';
import { buscarDuplicados, type Coincidencia } from './duplicados.js';
import { idDe, obraNueva } from './estado.js';
import {
  CONFIANZAS,
  ETAPAS,
  FASES,
  TIPOS_PROYECTO,
  type Obra,
} from './modelo.js';
import { Ternario } from './piezas.jsx';
import { GaleriaFotos } from '../componentes/GaleriaFotos.js';

/**
 * Alta y edicion de una obra, a mano.
 *
 * TODO SE ESCRIBE: no se pide la ubicacion del dispositivo, no se lee ningun
 * cartel, no se llama a ningun servicio de direcciones. La zona es lo unico
 * que coloca la obra en el mapa, y se elige de una lista cerrada porque una
 * zona escrita a mano de tres maneras rompe los filtros y los duplicados.
 *
 * Antes de guardar un alta nueva, se avisa de lo que se le parece. Se avisa;
 * no se impide, no se junta y no se borra: dos chalets contiguos del mismo
 * promotor son dos obras que se parecen muchisimo y son distintas.
 */

interface Props {
  /** Si viene, se edita; si no, es un alta. */
  obra?: Obra;
  /**
   * Un alta ya empezada: lo que se ha leido de la foto de una ficha de papel.
   * No es lo mismo que `obra`: esto sigue siendo un ALTA —se avisa de los
   * duplicados, se comprueba lo obligatorio— solo que con casillas rellenas.
   */
  borrador?: Obra;
  obras: Obra[];
  idioma: Idioma;
  /** Quien esta dando de alta, para poder asignarse la obra a si mismo. */
  quien: string;
  /**
   * Las fotos elegidas en el alta viajan aparte: la obra aun no existe en el
   * servidor y no se les puede colgar nada hasta que se guarde. Puede devolver
   * una promesa; mientras dura, el formulario dice que esta guardando.
   */
  onGuardar: (obra: Obra, fotos: File[]) => void | Promise<void>;
  onCancelar: () => void;
  onAbrirExistente: (id: string) => void;
}

const euros = (cents: number | null) => (cents === null ? '' : String(cents / 100));
const aCents = (texto: string): number | null => {
  const n = Number(texto.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};

/** Lo que devuelve la busqueda de una direccion. */
interface Lugar {
  etiqueta: string;
  lat: number;
  lon: number;
  municipio: string | null;
  zona: string | null;
  codigoPostal: string | null;
}

export function Formulario({
  obra,
  borrador: inicial,
  obras,
  idioma,
  quien,
  onGuardar,
  onCancelar,
  onAbrirExistente,
}: Props) {
  const editando = obra !== undefined;
  const partida = obra ?? inicial;
  const [borrador, setBorrador] = useState<Obra>(() => partida ?? obraNueva());
  const [valorTexto, setValorTexto] = useState(euros(partida?.valorMercadoCents ?? null));
  const [potencialTexto, setPotencialTexto] = useState(euros(partida?.potencialCents ?? null));
  const [etiquetasTexto, setEtiquetasTexto] = useState((partida?.etiquetas ?? []).join(', '));

  /*
    EL MOVIL Y EL CORREO, EN EL ALTA.

    Los contactos de una obra se llevan en su ficha, con su rol y su influencia,
    y ahi seguiran. Pero quien da de alta una obra desde la calle acaba de
    hablar con alguien y tiene su telefono en la mano: obligarle a guardar,
    abrir la ficha, ir a la pestana de contactos y anadirlo alli es la razon por
    la que ese telefono termina en una nota del movil y no en el CRM.

    Lo que se escribe aqui crea el PRIMER contacto de la obra. Si la obra ya
    tenia uno —se esta editando—, se actualiza ese en vez de anadir un segundo,
    que es lo que convierte una ficha en una lista de la misma persona repetida.
  */
  // El principal es el primero que NO es el promotor: el promotor tiene su campo.
  const contactoPrincipal = obra?.contactos.find((c) => c.rol !== 'PROMOTOR') ?? null;
  const [contactoTelefono, setContactoTelefono] = useState(contactoPrincipal?.telefono ?? '');
  const [contactoEmail, setContactoEmail] = useState(contactoPrincipal?.email ?? '');
  /*
    EL PROMOTOR, CON NOMBRE PROPIO EN EL ALTA. Es lo primero que se sabe de una
    obra —esta en el cartel de la valla— y lo que mas sirve para agruparlas.
    Se guarda como un contacto con rol Promotor, que es lo que ya era: asi sale
    tambien en Contactos y lo tiene en cuenta el aviso de duplicados.
  */
  const [promotorTexto, setPromotorTexto] = useState(
    partida?.contactos.find((c) => c.rol === 'PROMOTOR')?.nombre ?? '',
  );
  /* Fotos elegidas en un ALTA: se suben cuando la obra ya este guardada. */
  const [fotos, setFotos] = useState<File[]>([]);
  // Una URL por foto y no una por repintado: cada `createObjectURL` retiene el
  // fichero en memoria hasta que se revoca.
  const vistas = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => () => vistas.forEach((u) => URL.revokeObjectURL(u)), [vistas]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avisadas, setAvisadas] = useState<Coincidencia[] | null>(null);
  /*
    DONDE CAE LA OBRA.

    Antes se elegia zona de una lista de diez y la obra se plantaba en el centro
    de esa zona: media isla no estaba en la lista, y las obras de un mismo
    barrio salian apiladas en el mismo punto. Ahora se busca la direccion —o el
    codigo postal— y vuelve el municipio, el barrio y el punto exacto.

    Es una AYUDA, no un requisito: si el servicio no contesta o la calle no
    aparece, se escribe la zona a mano y la obra se guarda igual.
  */
  const [lugar, setLugar] = useState<Lugar | null>(null);
  const [opciones, setOpciones] = useState<Lugar[]>([]);
  const [localizando, setLocalizando] = useState(false);
  const [sinLugar, setSinLugar] = useState(false);
  const ids = {
    nombre: useId(),
    direccion: useId(),
    cp: useId(),
    zona: useId(),
    municipio: useId(),
    codigo: useId(),
    tipo: useId(),
    fase: useId(),
    progreso: useId(),
    fin: useId(),
    confianza: useId(),
    comentario: useId(),
    etapa: useId(),
    responsable: useId(),
    valor: useId(),
    potencial: useId(),
    origen: useId(),
    etiquetas: useId(),
    telefono: useId(),
    email: useId(),
    promotor: useId(),
    fotos: useId(),
  };

  const cambiar = (cambios: Partial<Obra>) => setBorrador((b) => ({ ...b, ...cambios }));

  /** Las zonas que se ofrecen: el catalogo mas las que ya usan otras obras. */
  const zonasSugeridas = useMemo(() => {
    const nombres = new Set<string>(Object.keys(ZONAS));
    for (const o of obras) if (o.zona.trim() !== '') nombres.add(o.zona.trim());
    return [...nombres].sort((a, b) => a.localeCompare(b, 'es'));
  }, [obras]);

  async function localizar(): Promise<void> {
    const consulta = [borrador.direccion, borrador.codigoPostal, borrador.municipio, borrador.zona]
      .map((x) => x.trim())
      .filter(Boolean)
      .join(', ');
    if (consulta.length < 3) return;
    setLocalizando(true);
    setSinLugar(false);
    try {
      const { lugares } = await api.mapaBuscar(consulta);
      if (lugares.length === 0) {
        setOpciones([]);
        setSinLugar(true);
        return;
      }
      setOpciones(lugares.length > 1 ? lugares : []);
      aplicarLugar(lugares[0]!);
    } catch {
      setSinLugar(true);
    } finally {
      setLocalizando(false);
    }
  }

  /** Lo encontrado rellena lo que falta; lo ya escrito a mano no se pisa. */
  function aplicarLugar(encontrado: Lugar): void {
    setLugar(encontrado);
    setOpciones([]);
    /*
      El municipio, del catalogo cuando la zona es conocida. El mapa de fuera
      devuelve a veces el nucleo en lugar del ayuntamiento —«es Camp de Mar» en
      vez de Andratx—, y como municipio de una obra eso no agrupa nada.
    */
    const zonaEncontrada = encontrado.zona ?? '';
    const municipio = ZONAS[zonaEncontrada]?.municipio ?? encontrado.municipio;
    cambiar({
      lat: encontrado.lat,
      lon: encontrado.lon,
      ...(municipio ? { municipio } : {}),
      ...(encontrado.zona && borrador.zona.trim() === '' ? { zona: encontrado.zona } : {}),
      ...(encontrado.codigoPostal && borrador.codigoPostal.trim() === ''
        ? { codigoPostal: encontrado.codigoPostal }
        : {}),
    });
  }

  const promotor = useMemo(
    () =>
      promotorTexto.trim() ||
      borrador.contactos.find((c) => c.rol === 'PROMOTOR' || c.rol === 'CONSTRUCTORA')?.nombre,
    [borrador.contactos, promotorTexto],
  );

  function componer(): Obra {
    /*
      EL PUNTO, POR ORDEN DE FIABILIDAD: lo localizado por la direccion primero;
      si no se localizo nada, el centro de la zona del catalogo; y si la zona se
      escribio a mano y no esta en el catalogo, lo que ya tuviera la obra.
    */
    const delCatalogo = ZONAS[borrador.zona.trim()];
    const punto = lugar ?? delCatalogo ?? null;
    const zonaEscrita = borrador.zona.trim();
    return {
      ...borrador,
      nombre: borrador.nombre.trim(),
      zona: zonaEscrita,
      direccion: borrador.direccion.trim(),
      codigo: borrador.codigo.trim() === '' ? `OB-${Date.now().toString(36).toUpperCase()}` : borrador.codigo.trim(),
      municipio:
        borrador.municipio.trim() ||
        lugar?.municipio ||
        delCatalogo?.municipio ||
        borrador.municipio,
      lat: punto?.lat ?? borrador.lat,
      lon: punto?.lon ?? borrador.lon,
      valorMercadoCents: aCents(valorTexto),
      potencialCents: aCents(potencialTexto),
      etiquetas: etiquetasTexto
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e !== ''),
      contactos: contactosCompuestos(),
    };
  }

  /**
   * Los contactos, con el movil y el correo del alta puestos en el primero.
   *
   * Sin nombre no se inventa uno: el contacto se llama como la obra, que es
   * exactamente lo que quien lo escribio tenia delante. Poner «Contacto» o
   * dejarlo vacio daria una ficha con una persona anonima dentro.
   */
  function contactosCompuestos(): Obra['contactos'] {
    return conPromotor(conPrincipal(borrador.contactos));
  }

  function conPrincipal(contactos: Obra['contactos']): Obra['contactos'] {
    const telefono = contactoTelefono.trim();
    const email = contactoEmail.trim();
    const i = contactos.findIndex((c) => c.rol !== 'PROMOTOR');

    if (i >= 0) {
      return contactos.map((c, j) =>
        j === i ? { ...c, telefono: telefono || null, email: email || null } : c,
      );
    }
    if (telefono === '' && email === '') return contactos;
    return [
      {
        id: idDe('cto'),
        nombre: borrador.nombre.trim(),
        rol: 'OTRO',
        telefono: telefono || null,
        email: email || null,
        web: null,
        idioma: null,
        canal: null,
        influencia: null,
        notas: null,
        sinContacto: false,
      },
      ...contactos,
    ];
  }

  /*
    El promotor se escribe en su campo y vive como contacto. Si ya habia uno se
    le cambia el nombre —no se crea otro—. Si se borra el nombre y ese contacto
    no tiene nada mas (ni telefono, ni correo, ni notas), se quita; si tiene
    algo, se deja: borrar un nombre no puede llevarse por delante un telefono.
  */
  function conPromotor(contactos: Obra['contactos']): Obra['contactos'] {
    const nombre = promotorTexto.trim();
    const i = contactos.findIndex((c) => c.rol === 'PROMOTOR');
    if (i >= 0) {
      const actual = contactos[i]!;
      if (nombre !== '') return contactos.map((c, j) => (j === i ? { ...c, nombre } : c));
      const vacio = !actual.telefono && !actual.email && !actual.notas;
      return vacio ? contactos.filter((_, j) => j !== i) : contactos;
    }
    if (nombre === '') return contactos;
    return [
      ...contactos,
      {
        id: idDe('cto'),
        nombre,
        rol: 'PROMOTOR',
        telefono: null,
        email: null,
        web: null,
        idioma: null,
        canal: null,
        influencia: 'DECIDE',
        notas: null,
        sinContacto: false,
      },
    ];
  }

  function intentarGuardar() {
    setError(null);
    if (borrador.nombre.trim().length < 2 || borrador.zona === '') {
      setError(t(idioma, 'obras.faltaNombre'));
      return;
    }
    const compuesta = componer();

    // Solo en el alta: al editar, la obra ya existe y se pareceria a si misma.
    if (!editando && avisadas === null) {
      const coincidencias = buscarDuplicados(
        {
          nombre: compuesta.nombre,
          direccion: compuesta.direccion,
          municipio: compuesta.municipio,
          zona: compuesta.zona,
          codigo: compuesta.codigo,
          ...(promotor ? { promotor } : {}),
        },
        obras,
        compuesta.id,
      );
      if (coincidencias.length > 0) {
        setAvisadas(coincidencias);
        return;
      }
    }

    void guardar(compuesta);
  }

  async function guardar(compuesta: Obra) {
    setGuardando(true);
    try {
      await onGuardar(compuesta, fotos);
    } finally {
      setGuardando(false);
    }
  }

  if (avisadas !== null && avisadas.length > 0) {
    return (
      <section className="tarjeta obras-duplicados">
        <h2>{t(idioma, 'obras.dupTitulo')}</h2>
        <p className="suave">{t(idioma, 'obras.dupExplicacion')}</p>
        <ul className="lista">
          {avisadas.map((c) => (
            <li key={c.obra.id} className="fila">
              <span className="crecer">
                <b>{c.obra.nombre}</b>
                <small>
                  {c.obra.codigo} · {c.obra.direccion} · {c.obra.zona}
                </small>
                <span className="fila-flex">
                  {[...new Set(c.motivos)].map((m) => (
                    <span key={m} className="pastilla neutra">
                      {t(idioma, m)}
                    </span>
                  ))}
                </span>
              </span>
              <button type="button" className="secundario" onClick={() => onAbrirExistente(c.obra.id)}>
                {t(idioma, 'obras.dupAbrir')}
              </button>
            </li>
          ))}
        </ul>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onCancelar}>
            {t(idioma, 'obras.dupCancelar')}
          </button>
          <button type="button" disabled={guardando} onClick={() => void guardar(componer())}>
            {t(idioma, 'obras.dupSeguir')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="tarjeta obras-formulario">
      <h2>{t(idioma, editando ? 'obras.editar' : 'obras.nuevaTitulo')}</h2>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <h3 className="rotulo">{t(idioma, 'obras.gIdentificacion')}</h3>
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor={ids.nombre}>{t(idioma, 'obras.nombreProvisional')}</label>
          <input
            id={ids.nombre}
            value={borrador.nombre}
            placeholder={t(idioma, 'obras.nombreEjemplo')}
            onChange={(e) => cambiar({ nombre: e.target.value })}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.codigo}>{t(idioma, 'obras.codigo')}</label>
          <input
            id={ids.codigo}
            value={borrador.codigo}
            onChange={(e) => cambiar({ codigo: e.target.value })}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.direccion}>{t(idioma, 'obras.direccion')}</label>
          <input
            id={ids.direccion}
            value={borrador.direccion}
            placeholder={t(idioma, 'obras.direccionEjemplo')}
            onChange={(e) => cambiar({ direccion: e.target.value })}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.cp}>{t(idioma, 'obras.codigoPostal')}</label>
          <input
            id={ids.cp}
            value={borrador.codigoPostal}
            inputMode="numeric"
            maxLength={5}
            onChange={(e) => cambiar({ codigoPostal: e.target.value })}
            // Con el codigo postal entero ya se puede decir el municipio: se
            // busca solo, sin tener que acordarse de pulsar nada.
            onBlur={() => {
              if (/^\d{5}$/.test(borrador.codigoPostal.trim()) && !lugar) void localizar();
            }}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.zona}>{t(idioma, 'obras.zona')}</label>
          {/*
            LA ZONA SE ESCRIBE, no se elige de una lista cerrada: la lista no
            puede tener todos los barrios de Palma, Calvia y Andratx, y lo que
            falta en ella es justo donde esta la obra que se intenta dar de alta.
            El catalogo sigue ahi, como sugerencia.
          */}
          <input
            id={ids.zona}
            list={`${ids.zona}-sugerencias`}
            value={borrador.zona}
            placeholder={t(idioma, 'obras.zonaEjemplo')}
            onChange={(e) => cambiar({ zona: e.target.value })}
          />
          <datalist id={`${ids.zona}-sugerencias`}>
            {zonasSugeridas.map((z) => (
              <option key={z} value={z}>
                {ZONAS[z]?.municipio ?? ''}
              </option>
            ))}
          </datalist>
          <small className="suave">{t(idioma, 'obras.zonaNota')}</small>
        </div>
        <div className="campo">
          <label htmlFor={ids.municipio}>{t(idioma, 'obras.municipio')}</label>
          <input
            id={ids.municipio}
            value={borrador.municipio}
            onChange={(e) => cambiar({ municipio: e.target.value })}
          />
        </div>
        <div className="campo campo-localizar">
          <span className="etiqueta-campo">{t(idioma, 'obras.situarTitulo')}</span>
          <button type="button" className="secundario" disabled={localizando} onClick={() => void localizar()}>
            <Icono nombre="pin" /> {t(idioma, localizando ? 'obras.situando' : 'obras.situar')}
          </button>
          {lugar && (
            <small className="suave">
              {t(idioma, 'obras.situada')}: {lugar.etiqueta}
            </small>
          )}
          {sinLugar && <small className="suave">{t(idioma, 'obras.noSituada')}</small>}
          {opciones.length > 0 && (
            <div className="lista-marcable">
              {opciones.map((o) => (
                <button
                  key={`${o.lat},${o.lon}`}
                  type="button"
                  className="enlace"
                  onClick={() => aplicarLugar(o)}
                >
                  {o.etiqueta}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="campo">
          <label htmlFor={ids.tipo}>{t(idioma, 'obras.tipoProyecto')}</label>
          <select
            id={ids.tipo}
            value={borrador.tipoProyecto}
            onChange={(e) => cambiar({ tipoProyecto: e.target.value as Obra['tipoProyecto'] })}
          >
            {TIPOS_PROYECTO.map((tp) => (
              <option key={tp} value={tp}>
                {t(idioma, `obras.tipo.${tp}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h3 className="rotulo">{t(idioma, 'obras.gConstruccion')}</h3>
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor={ids.fase}>{t(idioma, 'obras.fase')}</label>
          <select
            id={ids.fase}
            value={borrador.fase}
            onChange={(e) => cambiar({ fase: e.target.value as Obra['fase'] })}
          >
            {FASES.map((f) => (
              <option key={f} value={f}>
                {t(idioma, `obras.fase.${f}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={ids.progreso}>{t(idioma, 'obras.progreso')}</label>
          <input
            id={ids.progreso}
            type="number"
            min={0}
            max={100}
            value={borrador.progreso}
            onChange={(e) =>
              cambiar({ progreso: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
            }
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.fin}>{t(idioma, 'obras.finEstimada')}</label>
          <input
            id={ids.fin}
            type="date"
            value={borrador.finalizacionEstimada ?? ''}
            onChange={(e) => cambiar({ finalizacionEstimada: e.target.value || null })}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.confianza}>{t(idioma, 'obras.confianza')}</label>
          <select
            id={ids.confianza}
            value={borrador.confianza}
            onChange={(e) => cambiar({ confianza: e.target.value as Obra['confianza'] })}
          >
            {CONFIANZAS.map((c) => (
              <option key={c} value={c}>
                {t(idioma, `obras.confianza.${c}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="campo">
        <label htmlFor={ids.comentario}>{t(idioma, 'obras.comentarios')}</label>
        <textarea
          id={ids.comentario}
          rows={2}
          value={borrador.comentarioObra ?? ''}
          onChange={(e) => cambiar({ comentarioObra: e.target.value || null })}
        />
      </div>

      <h3 className="rotulo">{t(idioma, 'obras.gComercial')}</h3>
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor={ids.etapa}>{t(idioma, 'obras.etapaComercial')}</label>
          <select
            id={ids.etapa}
            value={borrador.etapa}
            onChange={(e) => cambiar({ etapa: e.target.value as Obra['etapa'] })}
          >
            {ETAPAS.map((et) => (
              <option key={et} value={et}>
                {t(idioma, `obras.etapa.${et}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={ids.responsable}>{t(idioma, 'obras.responsable')}</label>
          <select
            id={ids.responsable}
            value={borrador.responsable ?? ''}
            onChange={(e) => cambiar({ responsable: e.target.value || null })}
          >
            <option value="">{t(idioma, 'obras.sinResponsable')}</option>
            {responsables(obras, quien).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={ids.valor}>{t(idioma, 'obras.valorMercado')}</label>
          <input
            id={ids.valor}
            inputMode="decimal"
            value={valorTexto}
            placeholder="0"
            onChange={(e) => setValorTexto(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.potencial}>{t(idioma, 'obras.potencial')}</label>
          <input
            id={ids.potencial}
            inputMode="decimal"
            value={potencialTexto}
            placeholder="0"
            onChange={(e) => setPotencialTexto(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.origen}>{t(idioma, 'obras.origen')}</label>
          <input
            id={ids.origen}
            value={borrador.origen ?? ''}
            onChange={(e) => cambiar({ origen: e.target.value || null })}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.etiquetas}>{t(idioma, 'obras.etiquetas')}</label>
          <input
            id={ids.etiquetas}
            value={etiquetasTexto}
            onChange={(e) => setEtiquetasTexto(e.target.value)}
          />
          <small className="suave">{t(idioma, 'obras.etiquetasNota')}</small>
        </div>
        <div className="campo">
          <label htmlFor={ids.telefono}>{t(idioma, 'obras.contactoMovil')}</label>
          <input
            id={ids.telefono}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+34 600 000 000"
            value={contactoTelefono}
            onChange={(e) => setContactoTelefono(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.email}>{t(idioma, 'obras.contactoEmail')}</label>
          <input
            id={ids.email}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="nombre@empresa.com"
            value={contactoEmail}
            onChange={(e) => setContactoEmail(e.target.value)}
          />
          <small className="suave">{t(idioma, 'obras.contactoNota')}</small>
        </div>
        <div className="campo">
          <label htmlFor={ids.promotor}>{t(idioma, 'obras.promotor')}</label>
          <input
            id={ids.promotor}
            value={promotorTexto}
            placeholder={t(idioma, 'obras.promotorEjemplo')}
            onChange={(e) => setPromotorTexto(e.target.value)}
          />
        </div>
      </div>

      <h3 className="rotulo">{t(idioma, 'obras.gSituacion')}</h3>
      <div className="rejilla dos">
        <Ternario
          etiqueta={t(idioma, 'obras.propietarioConocido')}
          valor={borrador.situacion.propietarioConocido}
          idioma={idioma}
          onCambio={(v) => cambiar({ situacion: { ...borrador.situacion, propietarioConocido: v } })}
        />
        <Ternario
          etiqueta={t(idioma, 'obras.usoDelPropietario')}
          valor={borrador.situacion.usoDelPropietario}
          idioma={idioma}
          onCambio={(v) => cambiar({ situacion: { ...borrador.situacion, usoDelPropietario: v } })}
        />
        <Ternario
          etiqueta={t(idioma, 'obras.saldraAVenta')}
          valor={borrador.situacion.saldraAVenta}
          idioma={idioma}
          onCambio={(v) => cambiar({ situacion: { ...borrador.situacion, saldraAVenta: v } })}
        />
        <Ternario
          etiqueta={t(idioma, 'obras.yaEnVenta')}
          valor={borrador.situacion.yaEnVenta}
          idioma={idioma}
          onCambio={(v) => cambiar({ situacion: { ...borrador.situacion, yaEnVenta: v } })}
        />
      </div>

      {/*
        FOTOS DE LA OBRA. Cada subida queda con su fecha y con quien la hizo:
        dentro de unos meses se pone la de hoy al lado de la nueva y se ve cuanto
        ha avanzado. Al EDITAR, la obra ya existe y cada foto se sube al
        momento. En un ALTA todavia no existe: se eligen aqui, se ven, y se
        suben en cuanto la obra queda guardada.
      */}
      <h3 className="rotulo">{t(idioma, 'fotos.obra')}</h3>
      {editando && obra ? (
        <GaleriaFotos
          idioma={idioma}
          entidad="OBRA"
          entidadId={obra.id}
          puedeGestionar
          titulo={t(idioma, 'fotos.obra')}
        />
      ) : (
        <div className="campo">
          <label htmlFor={ids.fotos}>{t(idioma, 'fotos.adjuntar')}</label>
          <input
            id={ids.fotos}
            type="file"
            accept={FOTOS_ADMITIDAS}
            multiple
            onChange={(e) => {
              const elegidas = [...(e.target.files ?? [])].filter((f) =>
                f.type.startsWith('image/'),
              );
              setFotos((antes) => [...antes, ...elegidas]);
              e.target.value = '';
            }}
          />
          <small className="suave">{t(idioma, 'fotos.altaNota')}</small>
          {fotos.length > 0 && (
            <div className="galeria">
              {fotos.map((f, i) => (
                <figure key={`${f.name}-${i}`} className="galeria-foto">
                  <img src={vistas[i]} alt={f.name} />
                  <figcaption>
                    <b>{quien}</b>
                    <small>{t(idioma, 'fotos.alGuardar')}</small>
                  </figcaption>
                  <button
                    type="button"
                    className="icono-accion peligro galeria-quitar"
                    title={t(idioma, 'fotos.quitar')}
                    aria-label={t(idioma, 'fotos.quitar')}
                    onClick={() => setFotos((antes) => antes.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </figure>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="acciones">
        <button type="button" className="secundario" onClick={onCancelar} disabled={guardando}>
          {t(idioma, 'obras.cancelar')}
        </button>
        <button type="button" onClick={intentarGuardar} disabled={guardando}>
          {guardando
            ? t(idioma, fotos.length > 0 ? 'fotos.guardandoConFotos' : 'obras.guardando')
            : t(idioma, editando ? 'obras.guardarCambios' : 'obras.guardar')}
        </button>
      </div>
    </section>
  );
}

/* Lo que se ve bien en cualquier navegador, y lo que manda un iPhone. */
const FOTOS_ADMITIDAS = 'image/jpeg,image/png,image/webp,image/heic';
